/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'clsx';
import { useFormik } from 'formik';
import { sortBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  OutlinedInput,
} from '@mui/material';

import { SERVICE_TYPES, ServiceType } from 'shared';

import { HUModal2, HUModal2Props } from '../../../../components/HUModal2/HUModal2';
import { HUSpin } from '../../../../components/HUSpin/HUSpin';
import { SimpleErrorModal } from '../../../../components/SimpleErrorModal/SimpleErrorModal';
import { ArrowRoundedIcon } from '../../../../components/svg-icons/ArrowRoundedIcon';
import { HUAutocomplete } from '../../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../../elements/HUTextInput/utils';
import { Namespaces } from '../../../../i18n';
import { SUPlanApiService } from '../../../../services/SUPlanApiService';
import { useFormStyles } from '../../../../styles/form';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { StorageClass } from '../../../../types/product/StorageClass';
import { PlanStorageCreateRequest } from '../../../../types/su-plan/requests/PlanStorageCreateRequest';
import { PlanStorageUpdateResponse } from '../../../../types/su-plan/requests/PlanStorageUpdateResponse';
import { fetchStorageClasses } from '../../../../utils/queryFns';
import { useApiValidation } from '../../../../utils/useApiValidation';
import { useObjectState } from '../../../../utils/useObjectState';
import { useValidationTranslation } from '../../../../utils/useValidationTranslation';
import {
  CreatePlanStorageDialogValues,
  formInitialValues,
  validationSchema,
} from './CreatePlanStorageDialog.miscellaneous';

export type CreatePlanStorageDialogOnSuccessCallback = (params: { payload: PlanStorageUpdateResponse }) => any;

type CreatePlanStorageFormProps = Pick<HUModal2Props, 'open'> &
  Required<Pick<HUModal2Props, 'onCancel'>> & {
    className?: string;
    onSuccess: CreatePlanStorageDialogOnSuccessCallback;
  };

export function CreatePlanStorageDialog(props: CreatePlanStorageFormProps) {
  const { className, open, onCancel, onSuccess } = props;
  const formClasses = useFormStyles();
  const { t } = useTranslation([Namespaces.Admin]);
  const { vtr } = useValidationTranslation();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [errorModal, setErrorModal] = useObjectState<{
    isOpen: boolean;
    error: string;
  }>({ isOpen: false, error: '' });

  const storageClassesQuery = useQuery([fetchStorageClasses.key], fetchStorageClasses.fn);

  const serviceTypes = SERVICE_TYPES;

  const storageClasses = useMemo(
    () => sortBy(storageClassesQuery.data?.data.payload ?? [], ['id']),
    [storageClassesQuery.data],
  );

  const initialValues = useMemo<CreatePlanStorageDialogValues>(
    () => ({
      ...formInitialValues,
    }),
    [],
  );

  const { setApiError, getFieldApiError, resetFieldApiError } = useApiValidation();

  const onFormSubmit = useCallback(
    async (values: CreatePlanStorageDialogValues) => {
      try {
        setIsProcessing(true);
        const data: PlanStorageCreateRequest = {
          ...values,
        };
        const {
          data: { payload },
        } = await SUPlanApiService.createPlanStorage({ data });
        onSuccess({ payload });
      } catch (e) {
        const error = e as ApiClientError;
        const validation = setApiError(error);
        if (validation.errorType === 'unexpected' || validation.errorType === 'common') {
          setErrorModal({
            isOpen: true,
            error: `manage_plans.dialogs.errors.${validation.error ?? ''}`,
          });
        }
      } finally {
        setIsProcessing(false);
      }
    },
    [onSuccess, setApiError, setErrorModal],
  );

  const formikProps = useFormik<CreatePlanStorageDialogValues>({
    validationSchema,
    enableReinitialize: true,
    initialValues,
    onSubmit: onFormSubmit,
  });

  const { values, touched, errors, handleBlur, handleChange, handleSubmit, setFieldValue, submitForm, resetForm } =
    formikProps;

  useEffect(() => {
    if (!open) {
      resetForm({ values: { ...formInitialValues } });
    }
  }, [resetForm, open]);

  useEffect(() => {
    if (storageClasses) {
      const defaultStorageClass = storageClasses.find(s => s.isDefault);
      defaultStorageClass && void setFieldValue('storageClassId', defaultStorageClass.id);
    }
  }, [storageClasses, setFieldValue]);

  const selectedServiceType = useMemo(
    () => serviceTypes.find(type => type === values.serviceType) ?? null,
    [serviceTypes, values.serviceType],
  );

  const selectedStorageClass = useMemo(
    () => storageClasses.find(s => s.id === values.storageClassId) ?? null,
    [storageClasses, values.storageClassId],
  );

  const getError = (field: keyof CreatePlanStorageDialogValues): string => {
    return errors[field] || getFieldApiError(field) || '';
  };

  const hasError = (field: keyof CreatePlanStorageDialogValues): boolean => {
    return !!touched[field] && !!getError(field);
  };

  const onTextFieldChange = (name: keyof CreatePlanStorageDialogValues, event: React.ChangeEvent) => {
    handleChange(event);
    resetFieldApiError(name);
  };

  const getLabelKey = (key: string): string => {
    return `manage_plans.dialogs.labels.${key}`;
  };

  return (
    <HUModal2
      open={open}
      okButtonText={t('manage_plans.dialogs.buttons.submit')}
      okButtonProps={{ disabled: !formikProps.dirty || isProcessing }}
      cancelButtonText={t('manage_plans.dialogs.buttons.cancel')}
      onCancel={onCancel}
      onClose={onCancel}
      disableRestoreFocus
      onOk={submitForm}
      size="big"
      title={t('manage_plans.dialogs.titles.storage_plan.create')}
    >
      <HUSpin spinning={isProcessing} delay={50}>
        <form noValidate className={cx(className)} onSubmit={handleSubmit}>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('name')}
            variant="outlined"
          >
            <InputLabel htmlFor="name">{t(getLabelKey('name'))}</InputLabel>
            <OutlinedInput
              id="name"
              name="name"
              label={t(getLabelKey('name'))}
              error={hasError('name')}
              fullWidth
              type="text"
              value={values.name}
              onChange={onTextFieldChange.bind(null, 'name')}
              disabled={false}
              onBlur={handleBlur}
            />
            {hasError('name') && (
              <FormHelperText>
                {vtr(getError('name'), {
                  field: t(getLabelKey('name')),
                })}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('size')}
            variant="outlined"
          >
            <InputLabel htmlFor="name">{t(getLabelKey('size'))}</InputLabel>
            <OutlinedInput
              id="size"
              name="size"
              label={t(getLabelKey('size'))}
              error={hasError('size')}
              fullWidth
              type="number"
              value={values.size}
              onChange={onTextFieldChange.bind(null, 'size')}
              disabled={false}
              onBlur={handleBlur}
            />
            {hasError('size') && (
              <FormHelperText>
                {vtr(getError('size'), {
                  field: t(getLabelKey('size')),
                })}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('serviceType')}
            variant="outlined"
          >
            <InputLabel htmlFor="name">{t(getLabelKey('service'))}</InputLabel>
            <HUAutocomplete
              popupIcon={<ArrowRoundedIcon />}
              renderInput={renderParams => (
                <OutlinedInput
                  {...destructRenderInputParams(renderParams)}
                  name="serviceType"
                  label={t(getLabelKey('service'))}
                  onBlur={handleBlur}
                />
              )}
              options={serviceTypes}
              disableClearable={true}
              value={selectedServiceType ?? undefined}
              onBlur={handleBlur}
              onChange={(_e: any, value: ServiceType | null) => {
                if (value !== null) {
                  void setFieldValue('serviceType', value, true);
                  resetFieldApiError('serviceType');
                }
              }}
              getOptionLabel={v => t(`general:services.types.${v}.short`)}
            />
            {hasError('serviceType') && (
              <FormHelperText>
                {vtr(getError('serviceType'), {
                  field: t(getLabelKey('service')),
                })}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('storageClassId')}
            variant="outlined"
          >
            <InputLabel htmlFor="name">{t(getLabelKey('storage_class'))}</InputLabel>
            <HUAutocomplete
              popupIcon={<ArrowRoundedIcon />}
              renderInput={renderParams => (
                <OutlinedInput
                  {...destructRenderInputParams(renderParams)}
                  name="storageClassId"
                  label={t(getLabelKey('storage_class'))}
                />
              )}
              options={storageClasses}
              disableClearable={true}
              value={selectedStorageClass ?? undefined}
              onChange={(_e: any, value: StorageClass | null) => {
                if (value !== null) {
                  void setFieldValue('storageClassId', value && value.id, true);
                  resetFieldApiError('storageClassId');
                }
              }}
              onBlur={handleBlur}
              getOptionLabel={v => v.id}
              renderOption={(props, opt) => (
                <li {...props}>
                  {opt.id}
                  {opt.isDefault ? ` (${t(`${Namespaces.Common}:label.default`)})` : ''}
                </li>
              )}
            />
            {hasError('storageClassId') && (
              <FormHelperText>
                {vtr(getError('storageClassId'), {
                  field: t(getLabelKey('storage_class')),
                })}
              </FormHelperText>
            )}
          </FormControl>
          <div className={formClasses.checkboxes}>
            <FormControlLabel
              name="isVisible"
              checked={values.isVisible}
              onChange={handleChange}
              control={<Checkbox color="primary" />}
              label={t(getLabelKey('is_visible'))}
              labelPlacement="end"
              disabled={false}
            />
          </div>
          <div className={formClasses.hiddenSubmitBtn}>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              size="medium"
              disabled={!formikProps.dirty || isProcessing}
            >
              &nbsp;
            </Button>
          </div>
        </form>
        <SimpleErrorModal
          open={errorModal.isOpen}
          message={errorModal.error}
          t={t}
          onOk={() => setErrorModal({ isOpen: false })}
        />
      </HUSpin>
    </HUModal2>
  );
}
